import classNames from 'clsx';
import React, { useEffect } from 'react';
import BodyClassName from 'react-body-classname';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';

import FloatingWelcomeOffer from '@swf/components/FloatingWelcomeOffer/FloatingWelcomeOffer';
import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import ServiceUnavailable from '@swf/components/ServiceUnavailable/ServiceUnavailable';
import StarsSection from '@swf/components/StarsSection/StarsSection';
import useFirstRender from '@swf/core/hooks/useFirstRender';
import { select } from '@swf/core/store';
import type { Layout, Section } from '@swf/core/types/Layout';
import { getScrollingElem } from '@swf/core/utils';

import styles from './StarsLayout.scss';
import A2HS from './components/A2HS/A2HS';
import AppDownloadBanner from './components/AppDownloadBanner/AppDownloadBanner';
import BrowserNotification from './components/BrowserNotification/BrowserNotification';
import CookieNotification from './components/CookieNotification/CookieNotification';

export interface Props extends Layout {
  config: { background: string; backgroundFit: string; backgroundColor: string };
  sections: {
    header: Section;
    main: Section;
    footer: Section;
  };
}

const scrollingElement = getScrollingElem();

const StarsLayout: React.FC<Props> = ({
  config = { background: '', backgroundFit: '', backgroundColor: '' },
  sections
}) => {
  const pathname = useSelector(select.location.getPathname);
  const isSystemAvailable = useSelector(select.system.isAvailable);
  const firstRender = useFirstRender();
  const isEmbedded = useSelector(select.site.isEmbedded);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const showWebContent = !isEmbedded && !embeddedRoute;
  const isSportsVertical = useSelector(select.page.isSportsVertical);

  useEffect(() => {
    if (!firstRender && scrollingElement && !window.location.hash) {
      scrollingElement.scrollTop = 0;
    }
  }, [pathname, firstRender]);

  return (
    <>
      <Helmet>
        <html className={styles.html} />
      </Helmet>
      <BodyClassName className={styles.body} />
      {showWebContent && (
        <>
          <BrowserNotification />
          <AppDownloadBanner />
        </>
      )}
      <A2HS />
      {showWebContent && <StarsSection {...sections.header} preload />}
      <div
        className={classNames(styles.main, config.backgroundFit && styles[config.backgroundFit])}
        style={{
          ...(config.background && { backgroundImage: `url(${config.background})` }),
          ...(config.backgroundColor && { backgroundColor: config.backgroundColor })
        }}
      >
        {isSystemAvailable ? (
          <StarsSection {...sections.main} preload={3} />
        ) : (
          <>
            <CacheControl>public, max-age=5</CacheControl>
            <ServiceUnavailable />
          </>
        )}
      </div>
      {(showWebContent || isSportsVertical) && (
        <>
          <StarsSection {...sections.footer} />
          <CookieNotification />
          <FloatingWelcomeOffer />
        </>
      )}
    </>
  );
};

export default StarsLayout;
